<template>
    <UPopover
    v-model:open="open"
    :dismissible="false"
    :ui="{ content: 'w-(--reka-popper-anchor-width) p-2' }"
  >
    <template #anchor>
            <UInput  
              ref="searchInput"
              v-model="query" 
              placeholder="Search" 
              size="md" 
              icon="i-heroicons-magnifying-glass"
              aria-label="Search products"
              role="combobox"
              :aria-expanded="open"
              aria-autocomplete="list"
              aria-controls="search-results"
              :aria-activedescendant="activeIndex >= 0 ? `product-${products[activeIndex]?.id}` : undefined"
              class="w-full hidden lg:block max-w-xs"
              @focus="open = true" 
              @blur="handleInputBlur"
              @keydown.escape="handleInputEscape"
              @keydown.arrow-down.prevent="handleInputArrowDown"
              @keydown.arrow-up.prevent="handleInputArrowUp"
              @keydown.enter="handleInputEnter"
            >
            <template v-if="query.length" #trailing>
      <UButton
        color="neutral"
        variant="link"
        size="sm"
        icon="i-lucide-circle-x"
        aria-label="Clear search"
        id="clear-search-button"
        @blur="handleButtonBlur"
        @mousedown.prevent="clearSearch"
        @keydown.escape="handleInputEscape"
      />
    </template>
            </UInput>
    </template>
    <template #content>
        <div id="search-results" role="listbox" aria-label="Search results">
        <div v-if="!query">
            <p class="text-gray-500 p-4">Type to search for products.</p>
        </div>
        <div v-else>
            <div v-if="pending" class="flex flex-col *:border-b *:border-gray-200 *:last-of-type:border-0" aria-live="polite" aria-busy="true">
                <TinyProductCardSkeleton  />
            </div>
            <div v-else-if="error" class="p-4" role="alert">
                <UAlert
                    title="Search Error"
                    description="Unable to search products. Please try again."
                    icon="i-lucide-alert-circle"
                    color="error"
                    variant="soft"
                />
            </div>
            <div v-else class="flex flex-col *:border-b *:border-gray-200 *:last-of-type:border-0 *:hover:bg-gray-50" aria-live="polite">
                <p v-if="!products.length" class="text-gray-500 p-4" role="status">No products found.</p>
               <TinyProductCard 
                 v-for="(product, index) in products" 
                 :key="product.id" 
                 :product="product" 
                 :active="index === activeIndex"
                 :product-id="`product-${product.id}`"
                 role="option"
                 :aria-selected="index === activeIndex"
                  
               />
            </div>
        </div>
        </div>
    </template>
    </UPopover>
        
</template>

<script setup lang="ts">
import { ref } from 'vue'
import TinyProductCard from '~/components/molecules/TinyProductCard.vue'
import TinyProductCardSkeleton from '~/components/molecules/TinyProductCardSkeleton.vue'

const localePath = useLocalePath()
const open = ref(false)
const activeIndex = ref(-1)
const { query, products, pending, error } = useProductSearch({
    perPage: 5
})

function handleInputEscape() {
  open.value = false
  query.value = ''
  activeIndex.value = -1
}

function clearSearch() {
  query.value = ''
  activeIndex.value = -1
}

function handleInputArrowDown() {
  if (!products.value.length) return
  
  if (activeIndex.value < products.value.length - 1) {
    activeIndex.value++
  } else {
    activeIndex.value = 0 // Loop back to first
  }
}

function handleInputArrowUp() {
  if (!products.value.length) return
  
  if (activeIndex.value > 0) {
    activeIndex.value--
  } else if (activeIndex.value === 0) {
    activeIndex.value = products.value.length - 1 // Loop to last
  } else {
    activeIndex.value = products.value.length - 1
  }
}

function handleInputEnter(event: KeyboardEvent) {
  if (activeIndex.value >= 0 && activeIndex.value < products.value.length) {
    const product = products.value[activeIndex.value]
    if (product) {
      // Navigate to product page
      navigateTo(localePath({ name: 'product-slug', params: { slug: product.slug } }))
      event.preventDefault()

      query.value = ''
      open.value = false
      activeIndex.value = -1
    }
  }
}

watch(query, (newQuery) => {
  if (newQuery) {
    open.value = true
  }
  // Reset active index when search query changes
  activeIndex.value = -1
})

// Reset active index when products change
watch(products, () => {
  activeIndex.value = -1
})

const handleInputBlur = (event: FocusEvent) => {
  const clearButton = document.getElementById('clear-search-button');
  if (event.relatedTarget && clearButton && event.relatedTarget == clearButton) {
    return; // Do not close the popover if the clear button is focused
  }
  open.value = false;
};

const input = useTemplateRef('searchInput');

const handleButtonBlur = () => {
  if (products.value.length > 0) {
    input.value?.inputRef?.focus()
  } else {
     open.value = false;
  }
}
</script>